import React, { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useUniboxColors } from "../../useUniboxColors";

const ACTION_WIDTH = 68;
const OPEN_THRESHOLD = 34;
const DRAG_CLICK_THRESHOLD = 12;

/**
 * Fila con acción de eliminar al deslizar a la izquierda (touch o ratón).
 */
export default function SwipeableThreadRow({
  children,
  onDelete,
  enabled = true,
  isOpen = false,
  onOpen,
  onClose,
}) {
  const c = useUniboxColors();
  const [offset, setOffset] = useState(0);
  const [dragging, setDragging] = useState(false);
  const startX = useRef(0);
  const startY = useRef(0);
  const startOffset = useRef(0);
  const offsetRef = useRef(0);
  const axisLock = useRef(null);
  const dragDistance = useRef(0);
  const suppressClick = useRef(false);
  const pointerIdRef = useRef(null);
  const captureTargetRef = useRef(null);

  useEffect(() => {
    const next = isOpen ? -ACTION_WIDTH : 0;
    setOffset(next);
    offsetRef.current = next;
  }, [isOpen]);

  const clamp = (value) => Math.min(0, Math.max(-ACTION_WIDTH, value));

  const setOffsetSafe = (value) => {
    const next = typeof value === "function" ? value(offsetRef.current) : value;
    offsetRef.current = next;
    setOffset(next);
  };

  const releaseCapture = (target, pointerId) => {
    if (!target || pointerId == null) return;
    try {
      if (target.hasPointerCapture?.(pointerId)) {
        target.releasePointerCapture(pointerId);
      }
    } catch {
      /* ignore */
    }
  };

  const resetGesture = (target, pointerId) => {
    releaseCapture(target ?? captureTargetRef.current, pointerId ?? pointerIdRef.current);
    pointerIdRef.current = null;
    captureTargetRef.current = null;
    axisLock.current = null;
    dragDistance.current = 0;
    setDragging(false);
  };

  const handlePointerDown = (e) => {
    if (!enabled) return;
    if (e.button !== undefined && e.button !== 0) return;

    pointerIdRef.current = e.pointerId;
    captureTargetRef.current = e.currentTarget;
    startX.current = e.clientX;
    startY.current = e.clientY;
    startOffset.current = offsetRef.current;
    axisLock.current = null;
    dragDistance.current = 0;
    suppressClick.current = false;
  };

  const handlePointerMove = (e) => {
    if (!enabled || e.pointerId !== pointerIdRef.current) return;

    const dx = e.clientX - startX.current;
    const dy = e.clientY - startY.current;

    if (!axisLock.current) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      axisLock.current = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
      if (axisLock.current === "y") return;
      setDragging(true);
      e.currentTarget.setPointerCapture?.(e.pointerId);
    }

    if (axisLock.current !== "x") return;

    dragDistance.current = Math.abs(dx);
    suppressClick.current = dragDistance.current > DRAG_CLICK_THRESHOLD;
    setOffsetSafe(clamp(startOffset.current + dx));
  };

  const handlePointerEnd = (e) => {
    const target = e.currentTarget;
    const pointerId = e.pointerId;

    if (dragging) {
      setOffsetSafe((prev) => {
        const next = prev < -OPEN_THRESHOLD ? -ACTION_WIDTH : 0;
        if (next === 0) onClose?.();
        else onOpen?.();
        return next;
      });
    }

    if (dragDistance.current <= DRAG_CLICK_THRESHOLD) {
      suppressClick.current = false;
    }

    resetGesture(target, pointerId);
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    suppressClick.current = true;
    setOffsetSafe(0);
    onClose?.();
    onDelete?.();
  };

  const progress = Math.min(1, Math.abs(offset) / ACTION_WIDTH);
  const actionVisible = offset < -6;
  const isOpenSwipe = offset < -OPEN_THRESHOLD;

  return (
    <Box
      sx={{
        position: "relative",
        overflow: "hidden",
        borderRadius: c.radius,
        width: "100%",
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          background: c.isDark
            ? "linear-gradient(90deg, transparent 30%, rgba(255, 69, 58, 0.18) 100%)"
            : "linear-gradient(90deg, transparent 30%, rgba(255, 59, 48, 0.1) 100%)",
          opacity: progress,
          pointerEvents: "none",
          transition: dragging ? "none" : "opacity 0.2s ease",
        }}
      />

      <Box
        onClick={handleDeleteClick}
        aria-hidden={!actionVisible}
        aria-label="Eliminar conversación"
        sx={{
          position: "absolute",
          top: 0,
          right: 0,
          bottom: 0,
          width: ACTION_WIDTH,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: actionVisible ? "pointer" : "default",
          pointerEvents: actionVisible ? "auto" : "none",
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            bgcolor: "#ff3b30",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transform: `scale(${0.7 + progress * 0.3})`,
            opacity: progress,
            boxShadow:
              progress > 0.45
                ? "0 4px 14px rgba(255, 59, 48, 0.38)"
                : "none",
            transition: dragging
              ? "none"
              : "transform 0.22s ease, opacity 0.22s ease, box-shadow 0.22s ease",
            "&:active": {
              transform: `scale(${0.66 + progress * 0.28})`,
            },
          }}
        >
          <DeleteOutlineIcon sx={{ fontSize: 19 }} />
        </Box>
      </Box>

      <Box
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerEnd}
        onPointerCancel={handlePointerEnd}
        onClickCapture={(e) => {
          if (suppressClick.current) {
            e.preventDefault();
            e.stopPropagation();
          }
          suppressClick.current = false;
        }}
        sx={{
          width: "100%",
          transform: `translateX(${offset}px)`,
          transition: dragging ? "none" : "transform 0.24s cubic-bezier(0.33, 1, 0.68, 1)",
          touchAction: enabled ? "pan-y" : "auto",
          position: "relative",
          zIndex: 1,
          borderRadius: c.radius,
          overflow: "hidden",
          boxShadow: isOpenSwipe
            ? c.isDark
              ? "-6px 0 18px rgba(0, 0, 0, 0.28)"
              : "-3px 0 14px rgba(15, 23, 42, 0.1)"
            : "none",
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
